import {
    call,
    put,
    select,
    takeLatest,
} from 'redux-saga/effects';
import {
    AppAction,
    ErrorType,
} from './enum';
import { AppState } from './interface';
import {
    selectAppState,
    selectSelectedTariffId,
    selectTariffs,
} from './selector';
import { JarvisProducts } from '../../interface';
import {
    setCustomer,
    setError,
    setLoading,
    setTariffs,
} from './action';
import {
    ApiErrorResponseWithCode,
    createUrlWithQueryString,
    debugInfo,
    updateUrl,
} from '@vfde-sails/utils';
import { initTariffFootnotes } from '../../components/footnote';
import {
    getProductDetails,
    JarvisProductGroup,
    ProductDetailsResponse,
} from '@vfde-sails/availability';
import {
    Customer,
    getAccessToken,
    getCustomerFromUserInfo,
    getOnlineShopUserInfo,
    TokenResponse,
    UserInfoV4Response,
} from '@vfde-sails/login';
import { trackPageview } from '../../tracking/tracking';
import {
    trackFixnetError,
    TrackingErrorType,
} from '@vfde-sails/tracking';
import { QueryNames } from '../../enum';

/**
 * Tracks the pageview event
 */
export function* trackPageViewSaga () {
    const state: AppState = yield select(selectAppState);

    if (!state.error) {
        yield call(trackPageview, state);
    }
}

/**
 * Updates page url query with tariff selection
 */
export function* updatePageUrlSaga () {
    const selectedTariffId: string = yield select(selectSelectedTariffId);
    const urlWithQuery: string = yield call(createUrlWithQueryString, QueryNames.Product, selectedTariffId);

    yield call(updateUrl, urlWithQuery);
}

/**
 * Updates the page url query and tracks pageview after tariff selection
 */
export function* selectedTariffChangedSaga () {
    yield call(updatePageUrlSaga);
    yield call(trackPageViewSaga);
}

/**
 * Fetches and sets initial tariff details from Jarvis
 */
export function* getInitialTariffs () {
    const tariffs: JarvisProducts = yield select(selectTariffs);

    if (Object.values(tariffs).length) {
        return;
    }

    try {
        yield put(setLoading(true, getInitialTariffs.name));

        const { products }: ProductDetailsResponse = yield call(getProductDetails, [JarvisProductGroup.DslChange]);

        if (!products.dslChange) {
            throw new Error(ErrorType.Jarvis);
        }

        yield put(setTariffs(products.dslChange));
    }
    catch (error) {
        yield put(setError({ error: error as Error, type: ErrorType.Jarvis }));
        yield call(trackFixnetError, error as Error, TrackingErrorType.Technical);
        yield call(debugInfo, error);
    }
    finally {
        yield put(setLoading(false, getInitialTariffs.name));
    }
}

/**
 * Initializes the footnotes
 */
export function* initFootnoteSaga () {
    const tariffs: JarvisProducts = yield select(selectTariffs);

    yield call(initTariffFootnotes, tariffs);
}

/**
 * Gets DXL user info then convert it to Customer interface then return it
 */
export function* collectCustomerInfo (accessToken: TokenResponse | null) {
    if (!accessToken) {
        return null;
    }

    const userInfoResponse: UserInfoV4Response | null = yield call(getOnlineShopUserInfo, accessToken);
    const [userInfo] = userInfoResponse || [];

    if (!userInfo) {
        return null;
    }

    const customer: Customer = yield call(getCustomerFromUserInfo, userInfo);

    return customer;
}

/**
 * Gets the customer data if logged in case, otherwise null.
 */
export function* getCustomer () {
    try {
        const accessToken: TokenResponse | null = yield call(getAccessToken);
        const customer: Customer | null = yield call(collectCustomerInfo, accessToken);

        return customer;
    }
    catch (error) {
        if (!(error instanceof ApiErrorResponseWithCode)) {
            throw error;
        }

        if (error.status !== 401) {
            throw error;
        }

        const accessToken: TokenResponse | null = yield call(getAccessToken);
        const customer: Customer | null = yield call(collectCustomerInfo, accessToken);

        return customer;
    }
}

/**
 * Checks the login state and gets the customer data if logged in
 */
export function* loginSaga () {
    try {
        yield put(setLoading(true, loginSaga.name));

        const customer: Customer | null = yield call(getCustomer);

        if (!customer) {
            return;
        }

        yield put(setCustomer(customer));
    }
    catch (error) {
        yield put(setError({ error: error as Error, type: ErrorType.Login }));
        yield call(trackFixnetError, error as Error, TrackingErrorType.Login);
        yield call(debugInfo, error);
    }
    finally {
        yield put(setLoading(false, loginSaga.name));
    }
}

/**
 * Initializes the state of the application
 */
export function* initStoreSaga () {
    yield call(getInitialTariffs);
    yield call(initFootnoteSaga);
    yield call(loginSaga);
    yield call(trackPageViewSaga);
}

/**
 * App Container saga
 */
export default function* saga () {
    yield takeLatest(AppAction.InitStore, initStoreSaga);
    yield takeLatest(AppAction.SetSelectedTariffId, selectedTariffChangedSaga);
}
